/**
 * Fonte única do padrão de nome de arquivo e de pasta na exportação de faturas.
 *
 * O Financeiro combinou o formato `{nº do mês}-{UC}-{Cidade}-{Origem}.pdf`, com
 * uma pasta por unidade e as subpastas `Equatorial` e a de quem emitiu o boleto
 * (`Vigor`, ou o nome da empresa parceira) dentro dela. Fica
 * num módulo próprio (e não dentro do ExportZipModal) porque é regra de negócio
 * conferida por gente de fora do time e porque só assim dá para testar sem JSX.
 */

/**
 * Quem emitiu o arquivo — vira o nome da subpasta e o sufixo do arquivo.
 *
 * `'Equatorial'` é a fatura da distribuidora. O boleto sai como `'Vigor'` quando
 * a cobrança é da própria Vigor e com o nome da empresa parceira quando é ela
 * quem emite (hoje só a Gran) — o Financeiro precisa distinguir os dois na
 * conferência, e um boleto da parceira dentro de uma pasta `Vigor` mente sobre
 * quem emitiu.
 */
export type OrigemFatura = string;

export const ORIGEM_DISTRIBUIDORA = 'Equatorial';
export const ORIGEM_VIGOR = 'Vigor';

export interface FaturaNomeavel {
  uc_codigo: string;
  mes_referencia: string;
  end_cidade?: string | null;
}

export const MES_NUMERO: Record<string, number> = {
  JAN: 1, FEV: 2, MAR: 3, ABR: 4, MAI: 5, JUN: 6,
  JUL: 7, AGO: 8, SET: 9, OUT: 10, NOV: 11, DEZ: 12,
};

const RE_ACENTO = /[\u0300-\u036f]/g;
/** Caracteres que o Windows recusa em nome de arquivo/pasta. */
const RE_INVALIDO = /[\\/:*?"<>|]/g;

/** `'AGO/2026'` → `8`. Devolve 0 quando o mês não é reconhecido. */
export const numeroDoMes = (mesReferencia: string): number =>
  MES_NUMERO[(mesReferencia || '').split('/')[0]?.trim().toUpperCase()] ?? 0;

/** Tira acento e o que o sistema de arquivos não aceita. */
export const limparNome = (texto: string): string =>
  (texto || '').normalize('NFD').replace(RE_ACENTO, '').replace(RE_INVALIDO, '').trim();

/** Cidade da fatura, já limpa. `SEM-CIDADE` quando o cadastro não tem o campo. */
export const cidadeDaFatura = (f: FaturaNomeavel): string =>
  limparNome(f.end_cidade || '') || 'SEM-CIDADE';

/**
 * Pasta da unidade — leva a UC junto com a cidade de propósito. Há UCs distintas
 * na mesma cidade (nas geradoras, duas em Morrinhos e duas em Buriti Alegre); uma
 * pasta só com o nome da cidade misturaria os arquivos das duas.
 */
export const pastaDaUnidade = (f: FaturaNomeavel): string =>
  `${limparNome(f.uc_codigo)} - ${cidadeDaFatura(f)}`;

/**
 * Origem já limpa. Desde que o nome da parceira entrou aqui, a origem é texto de
 * cadastro e não mais um literal — sem isto uma razão social com `/` abriria uma
 * subpasta extra dentro do ZIP.
 */
export const limparOrigem = (origem: OrigemFatura): string =>
  limparNome(origem) || ORIGEM_VIGOR;

/** `{nº mês}-{UC}-{Cidade}-{Origem}.pdf` */
export const nomeArquivoFatura = (f: FaturaNomeavel, origem: OrigemFatura): string =>
  `${numeroDoMes(f.mes_referencia)}-${limparNome(f.uc_codigo)}-${cidadeDaFatura(f)}-${limparOrigem(origem)}.pdf`;

/** Caminho completo dentro do ZIP, já com a subpasta da origem. */
export const caminhoNoZip = (f: FaturaNomeavel, origem: OrigemFatura): string =>
  `${pastaDaUnidade(f)}/${limparOrigem(origem)}/${nomeArquivoFatura(f, origem)}`;
